// DiscoverBumps — right-edge drawer on public silo pages.
// iter-31 Phase 1 of the Right-Side Bumps funnel.
//
// Two sections:
//   1. Discover — 3..20 sibling silos ranked by same-template first (admin-tunable
//      via platform_settings.discovery_slot_count).
//   2. Invite + Share this conversation — personal invite link with the user's
//      visitor_sig baked in (/s/<slug>?ref=<sig>), a Copy button, a "Share…"
//      button that triggers the native Web Share API (iOS/Android share sheet
//      with their contacts, Messages, Mail, etc.), and a SEPARATE "Share this
//      conversation" button that shares the current thread's link WITHOUT the
//      affiliate framing — for pure content sharing.
//
// Design contract: mirror the left-edge SideBumps visual pattern (thin vertical
// tab on the screen edge, clickable to slide out a ~340px panel).

import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, Share, CaretLeft, CaretRight, Copy, ChatCircle, Link as LinkIcon, CurrencyDollar, Sparkle } from "@phosphor-icons/react";
import * as Phosphor from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, getToken, getUser } from "@/lib/api";
import { getRefSig } from "@/lib/attribution";
import { useTabsOff, registerHiddenTabs, useOpenTabListener } from "@/lib/tabToggle";
import AffiliateExplainer from "@/components/AffiliateExplainer";
import EarningsDisclaimer from "@/components/EarningsDisclaimer";
import { useBumpConfig, slotToTopPct, COLOR_SLOT_VAR, SHAPE_CLASS_RIGHT } from "@/lib/bumpConfig";
import SearchBar from "@/components/SearchBar";

const SIG_COOKIE = "mysilo_visitor_sig";

function getVisitorSig() {
  // Authed creators share under their user_id as the sig (stable, dedupe-safe).
  const u = getUser();
  if (u?.id) return u.id;
  // Anon visitors — the cookie is set by PublicSilo on first mount.
  const match = document.cookie.split("; ").find((c) => c.startsWith(SIG_COOKIE + "="));
  return match ? match.split("=")[1] : "";
}

export default function DiscoverBumps({ silo, conversationSessionId = "", loadedBookmarkId = "", hasConversation = false, onShareConversation, onRequireSignup }) {
  const nav = useNavigate();
  const isAuthed = !!getToken();
  const user = getUser();
  const [openDiscover, setOpenDiscover] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);
  // Jul 2026 (Randy) — "Turn off tabs" support.
  const tabsOff = useTabsOff();
  const [showAffiliateExplainer, setShowAffiliateExplainer] = useState(false);
  const [silos, setSilos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sig, setSig] = useState("");
  // P0 Feb 7 fix — invite link now uses the user's permanent referral_code
  // (8-char short code) instead of their visitor_sig cookie UUID. The cookie
  // was being baked into shared links, which meant: (a) ugly 36-char UUIDs
  // in the URL, (b) seed broke the moment the user cleared cookies or
  // switched browsers because the cookie isn't bound to a user_id by itself,
  // (c) anonymous orphan rows were created in the referrals table.
  // Now we pull /me/referral-link once per mount and use the short code.
  const [referralCode, setReferralCode] = useState("");
  const [leaderboard, setLeaderboard] = useState(null);

  // iter-35g: bump positions/dims/style come from the platform-global config.
  const { cfg: bumpCfg, viewport: bumpVp } = useBumpConfig();
  const sideCfg = bumpCfg ? (bumpCfg.right?.[bumpVp] || { bumps: [], max_slots: 8 }) : null;
  const cfgById = useMemo(() => {
    const map = {};
    (sideCfg?.bumps || []).forEach((b) => { map[b.id] = b; });
    return map;
  }, [sideCfg]);
  const discoverCfg = cfgById["discover"];
  const inviteCfg = cfgById["invite"];

  useEffect(() => {
    const tabs = [];
    if (discoverCfg) tabs.push({ key: "right-discover", label: discoverCfg.name || "Discover" });
    if (inviteCfg) tabs.push({ key: "right-invite", label: inviteCfg.name || "Invite" });
    return registerHiddenTabs("discover-bumps", tabs);
  }, [discoverCfg, inviteCfg]);
  useOpenTabListener((key) => {
    if (key === "right-discover") { setOpenDiscover(true); setOpenInvite(false); }
    if (key === "right-invite") { setOpenInvite(true); setOpenDiscover(false); }
  });

  useEffect(() => { setSig(getVisitorSig()); }, []);

  // Pull the user's permanent referral_code so the invite link uses it
  // instead of the visitor_sig cookie. /me/referral-link is idempotent
  // (creates one if missing) and only callable when authed; anonymous
  // visitors fall back to the silo URL with no ?ref= which is correct
  // behaviour (anon visitors aren't earning credits anyway).
  useEffect(() => {
    if (!isAuthed) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/me/referral-link");
        if (!cancel) setReferralCode(r.data?.referral_code || "");
      } catch { /* swallow — falls back to plain link */ }
    })();
    return () => { cancel = true; };
  }, [isAuthed]);

  // Quiet social-proof one-liner, fetched only when the Invite panel is opened.
  // Hidden entirely when backend returns visible:false (no real referrers yet).
  useEffect(() => {
    if (!openInvite || leaderboard !== null) return;
    (async () => {
      try {
        const r = await api.get("/public/referrals/leaderboard");
        setLeaderboard(r.data || { visible: false });
      } catch { setLeaderboard({ visible: false }); }
    })();
  }, [openInvite, leaderboard]);

  const fetchDiscovery = useCallback(async () => {
    if (silos.length > 0) return;
    setLoading(true);
    try {
      const r = await api.get(`/public/discovery?current_slug=${encodeURIComponent(silo.slug)}${sig ? `&visitor_sig=${encodeURIComponent(sig)}` : ""}`);
      setSilos(r.data?.silos || []);
    } catch { /* silent — fall back to empty */ }
    finally { setLoading(false); }
  }, [silo.slug, sig, silos.length]);

  useEffect(() => {
    if (openDiscover) fetchDiscovery();
  }, [openDiscover, fetchDiscovery]);

  // Sprint Feb 8, 2026 — three-tier sharer-seed priority. Every share URL
  // emitted from this component now carries a ?ref= seed when one can be
  // determined, in this order:
  //   1. Logged-in user's permanent referral_code (creators + fans build
  //      their own referral base — anyone they bring in is theirs)
  //   2. Anonymous visitor's stored mysilo_ref_sig cookie (carries the
  //      ORIGINAL inviter's code forward — anons are transparent conduits)
  //   3. Empty (true cold visit — nothing to propagate)
  //
  // Bug fixed: previously `conversationUrl` was always unseeded, so when
  // someone tapped "Share this conversation" the recipient landed
  // un-attributed and fell back to the silo owner. Now both URLs share
  // the same seed-resolution logic.
  const sharerCode = referralCode || getRefSig();
  const seedQs = sharerCode ? `?ref=${encodeURIComponent(sharerCode)}` : "";
  const inviteUrl = `${window.location.origin}/s/${silo.slug}${seedQs}`;
  // Jun 2026 (Randy) — "Share this conversation" now carries the ACTUAL thread.
  // Priority: a LOADED BOOKMARK (?bookmark=<id>) wins — it travels with its saved
  // 7-day audio so the recipient gets the bright-green free play; otherwise a live
  // session (?session=<id>) preloads the current thread. Falls back to the bare
  // seeded silo link only when there's genuinely no thread to carry.
  const threadParam = loadedBookmarkId
    ? `bookmark=${encodeURIComponent(loadedBookmarkId)}`
    : (hasConversation && conversationSessionId ? `session=${encodeURIComponent(conversationSessionId)}` : "");
  const conversationUrl = threadParam
    ? `${window.location.origin}/s/${silo.slug}${seedQs}${seedQs ? "&" : "?"}${threadParam}`
    : `${window.location.origin}/s/${silo.slug}${seedQs}`;

  // Jun 2026 (Randy) — an anonymous (not-yet-signed-up) sharer has no referral
  // attribution. Instead of a dead-end, the three share buttons pop the sign-up
  // CTA so they become a documented member first (and their shares get seeded
  // to them). The RECIPIENT may still be anonymous — only the SENDER is nudged.
  const guardShare = (proceed) => {
    if (!isAuthed) { onRequireSignup?.(); return; }
    proceed();
  };

  const copyToClipboard = async (text, label = "Link") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually.");
    }
  };

  const nativeShare = async (url, title, text) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch (e) {
        // User canceled — no toast
        if (e.name !== "AbortError") toast.error("Share failed.");
      }
    } else {
      // Fallback: copy to clipboard
      await copyToClipboard(url, "Link");
    }
  };

  // Jun 2026 (Randy — 100% safe referral sharing). The platform no longer
  // writes the words for an incentivised share. Referral credits are
  // "consideration" under the FTC's rule, which makes US the initiator of any
  // commercial message we compose for the sender — and we cannot attach a
  // working opt-out to mail leaving someone else's inbox. So we hand over the
  // link and nothing else; the sender writes their own note in their own voice.

  return (
    <>
      {/* Right-edge tabs — iter-35g: each tab is its own `position: fixed`
          element so its `top: <pct>` resolves against the viewport height
          (a `fixed` parent with no explicit height collapses to 0, which
          made percentages resolve to 0 and pushed children off-screen). */}
      {discoverCfg && !tabsOff && (() => {
        const top = slotToTopPct(discoverCfg.slot, sideCfg.max_slots);
        const colors = COLOR_SLOT_VAR[discoverCfg.color_slot] || COLOR_SLOT_VAR.inverse;
        const shapeClass = SHAPE_CLASS_RIGHT[discoverCfg.shape] || SHAPE_CLASS_RIGHT["tab-classic"];
        const PhosIcon = Phosphor[discoverCfg.icon] || Phosphor.Compass;
        return (
          <button
            onClick={() => { setOpenDiscover((v) => !v); setOpenInvite(false); }}
            data-testid="discover-tab"
            aria-label="Open discover panel"
            aria-expanded={openDiscover}
            className={`fixed right-0 z-30 flex flex-col items-center justify-center gap-1.5 hover:opacity-90 transition-colors ${shapeClass}`}
            style={{
              top, transform: "translateY(-50%)",
              width: `${discoverCfg.width}px`, height: `${discoverCfg.height}px`,
              background: colors.bg, color: colors.fg,
              borderColor: "rgba(0,0,0,0.08)",
            }}
          >
            <PhosIcon size={Math.max(12, discoverCfg.text_size + 4)} weight="duotone" />
            <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: `${discoverCfg.text_size}px` }}
              className="uppercase tracking-[0.18em] font-medium whitespace-nowrap overflow-hidden">{discoverCfg.name}</span>
          </button>
        );
      })()}
      {inviteCfg && !tabsOff && (() => {
        const top = slotToTopPct(inviteCfg.slot, sideCfg.max_slots);
        const colors = COLOR_SLOT_VAR[inviteCfg.color_slot] || COLOR_SLOT_VAR.primary;
        const shapeClass = SHAPE_CLASS_RIGHT[inviteCfg.shape] || SHAPE_CLASS_RIGHT["tab-classic"];
        const PhosIcon = Phosphor[inviteCfg.icon] || Phosphor.Share;
        return (
          <button
            onClick={() => { setOpenInvite((v) => !v); setOpenDiscover(false); }}
            data-testid="invite-tab"
            aria-label="Open invite panel"
            aria-expanded={openInvite}
            className={`fixed right-0 z-30 flex flex-col items-center justify-center gap-1.5 hover:opacity-90 transition-colors ${shapeClass}`}
            style={{
              top, transform: "translateY(-50%)",
              width: `${inviteCfg.width}px`, height: `${inviteCfg.height}px`,
              background: colors.bg, color: colors.fg,
              borderColor: "rgba(0,0,0,0.08)",
            }}
          >
            <PhosIcon size={Math.max(12, inviteCfg.text_size + 4)} weight="duotone" />
            <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: `${inviteCfg.text_size}px` }}
              className="uppercase tracking-[0.18em] font-medium whitespace-nowrap overflow-hidden">{inviteCfg.name}</span>
          </button>
        );
      })()}

      <AnimatePresence>
        {(openDiscover || openInvite) && (
          <motion.div
            key="bumps-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => { setOpenDiscover(false); setOpenInvite(false); }}
            className="fixed inset-0 z-[79] bg-transparent"
            data-testid="bumps-backdrop"
          />
        )}
        {openDiscover && discoverCfg && (
          <motion.aside
            key="discover-panel"
            initial={{ x: 360, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 360, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 28 }}
            onClick={() => setOpenDiscover(false)}
            className="fixed right-0 inset-y-0 tpl-surface tpl-text border-l hairline shadow-2xl z-[80] flex flex-col overflow-hidden cursor-pointer"
            style={{ width: "min(384px, 92vw)" }}
            data-testid="discover-panel"
          >
            <div className="px-5 py-4 border-b hairline flex items-center tpl-text" style={{ background: "var(--tpl-text)", color: "var(--tpl-surface)" }}>
              <div className="flex items-center gap-2">
                <Compass size={16} weight="duotone" />
                <div className="font-serif-display text-base">Discover</div>
              </div>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] opacity-50">Tap anywhere to close</span>
            </div>
            <div
              className="flex-1 overflow-y-auto p-4 space-y-4 cursor-default"
              onClick={(e) => {
                if (e.target.closest("button, a, input, textarea, label")) {
                  e.stopPropagation();
                }
              }}
            >
              {/* Feb 18, 2026 — Search bar at the TOP of the Discover drawer.
                  Lets fans find any creator or silo across the platform —
                  not just the recommended ones below. */}
              <div className="pb-3 border-b hairline">
                <div className="text-[10px] uppercase tracking-[0.22em] tpl-muted mb-2">
                  Search the platform
                </div>
                <SearchBar variant="inline" placeholder="Find a creator or silo…" />
              </div>

              {/* Recommended silos — pushed below the search */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] tpl-muted mb-2">
                  Other digital twins you might enjoy
                </div>
                <div className="space-y-3">
                  {loading && <div className="text-sm tpl-muted">Loading…</div>}
                  {!loading && silos.length === 0 && (
                    <div className="text-sm tpl-muted italic">No other silos yet. Be the first to invite a creator!</div>
                  )}
                  {silos.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => { setOpenDiscover(false); nav(`/s/${s.slug}`); }}
                      data-testid={`discover-card-${s.slug}`}
                      className="w-full text-left tpl-surface border hairline rounded-lg p-3 hover:border-[#4A5D4E] transition-colors group"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm font-medium tpl-text truncate">{s.name}</div>
                        <span className="text-[9px] uppercase tracking-[0.2em] tpl-muted shrink-0 ml-2">{s.template}</span>
                      </div>
                      {s.tagline && <div className="text-xs tpl-muted line-clamp-2">{s.tagline}</div>}
                      {s.interest_matches > 0 && (
                        <div data-testid={`discover-match-${s.slug}`} className="mt-1.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] text-[#4A5D4E] bg-[#4A5D4E]/10 rounded-full px-2 py-0.5">
                          <Sparkle size={9} weight="fill" /> matches {s.interest_matches} of your interest{s.interest_matches === 1 ? "" : "s"}
                        </div>
                      )}
                      <div className="text-[10px] text-[#4A5D4E] mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Open silo →</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.aside>
        )}

        {openInvite && inviteCfg && (
          <motion.aside
            key="invite-panel"
            initial={{ x: 360, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 360, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 28 }}
            onClick={() => setOpenInvite(false)}
            className="fixed right-0 inset-y-0 tpl-surface tpl-text border-l hairline shadow-2xl z-[80] flex flex-col overflow-hidden cursor-pointer"
            style={{ width: "min(384px, 92vw)" }}
            data-testid="invite-panel"
          >
            <div className="px-5 py-4 border-b hairline flex items-center" style={{ background: "var(--tpl-accent-color, #4A5D4E)", color: "#FDFCF8" }}>
              <div className="flex items-center gap-2">
                <Share size={16} weight="duotone" />
                <div className="font-serif-display text-base">Share · Invite</div>
              </div>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] opacity-50">Tap anywhere to close</span>
            </div>
            <div
              className="flex-1 overflow-y-auto p-5 space-y-6 cursor-default"
              onClick={(e) => {
                // iter-35f: tap-anywhere-to-close, EXCEPT when the click
                // landed on an interactive element (button, link, input).
                // The button's own onClick already ran; we just don't want
                // it to bubble up to the wrapper's setOpenInvite(false).
                if (e.target.closest("button, a, input, textarea, label")) {
                  e.stopPropagation();
                }
              }}
            >
              {/* Share this conversation — pure content share (no affiliate framing). */}
              <section data-testid="invite-share-conversation">
                <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-2 flex items-center gap-1">
                  <ChatCircle size={12} weight="duotone" /> Share this conversation
                </div>
                <p className="text-xs text-[#6B6A68] mb-3">
                  Send this conversation to someone who'd enjoy it. They'll open it to this exact thread — and can listen to the whole thing — no sign-up needed on their end.
                </p>
                <div className="flex gap-2 flex-nowrap">
                  <button
                    onClick={() => guardShare(() => onShareConversation?.())}
                    data-testid="invite-share-conversation-open"
                    className="px-4 py-2 text-xs font-semibold rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5 shrink-0 transition-colors"
                  >
                    <Share size={13} weight="duotone" /> {hasConversation ? "Share this conversation" : "Share this silo"}
                  </button>
                </div>
              </section>

              <div className="border-t hairline" />

              {/* iter-33: Invite a creator — affiliate framing.
                  Gated on is_paid_creator. Anonymous + non-paid fans see a
                  greyed-out preview with an "Affiliate Creator" upsell.
                  Paid Creators see the full functional invite. */}
              {(user?.is_paid_creator || user?.role === "super_admin") ? (
                <section data-testid="invite-affiliate-section">
                  <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-2 flex items-center gap-1">
                    <CurrencyDollar size={12} weight="duotone" /> Invite a creator — earn credits
                  </div>
                  <p className="text-xs text-[#6B6A68] mb-3 leading-relaxed">
                    Know someone who'd build a great AI twin? Share your personal link.
                    When they sign up as a paid creator within <strong>30 days</strong>, you earn{" "}
                    <strong>platform credit — withdrawable as real cash</strong> once your payout
                    is set up.
                  </p>
                  <div className="mb-3"><EarningsDisclaimer variant="short" /></div>
                  <div className="bg-white border hairline rounded-md p-2.5 mb-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1">Your personal invite link</div>
                    <div className="text-[11px] font-mono text-[#1C1B1A] break-all select-all" data-testid="invite-link-text">{inviteUrl}</div>
                  </div>
                  <div className="flex gap-2 flex-nowrap">
                    <button
                      onClick={() => copyToClipboard(inviteUrl, "Invite link")}
                      data-testid="invite-copy-link"
                      className="px-3 py-2 text-xs rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1 shrink-0"
                    >
                      <LinkIcon size={12} /> Copy invite
                    </button>
                    <button
                      onClick={() => nativeShare(inviteUrl)}
                      data-testid="invite-share-link"
                      className="px-3 py-2 text-xs rounded-md bg-white border hairline hover:border-[#4A5D4E] flex items-center gap-1 shrink-0"
                    >
                      <Share size={12} /> Share…
                    </button>
                  </div>
                  {/* Quiet social proof — one line, no flash. Hidden until there's real data. */}
                  {leaderboard?.visible && (
                    <div className="mt-4 pt-3 border-t hairline" data-testid="invite-leaderboard-line">
                      <div className="text-[10px] text-[#6B6A68] leading-relaxed">
                        <span className="text-[#4A5D4E] font-medium">{leaderboard.period_label}:</span>{" "}
                        top referrer earned <span className="text-[#1C1B1A] font-medium tabular-nums">${(leaderboard.top_earned_cents/100).toFixed(2)}</span>
                        {leaderboard.top_awarded_count ? ` · ${leaderboard.top_awarded_count} creator${leaderboard.top_awarded_count === 1 ? "" : "s"} invited` : ""}
                        {leaderboard.platform_total_cents && leaderboard.platform_total_cents !== leaderboard.top_earned_cents ? (
                          <> · platform-wide <span className="tabular-nums">${(leaderboard.platform_total_cents/100).toFixed(2)}</span> across {leaderboard.platform_awarded_count}</>
                        ) : null}
                      </div>
                    </div>
                  )}
                </section>
              ) : (
                /* Greyed-out non-creator preview with Affiliate Creator upsell. */
                <section data-testid="invite-affiliate-locked" className="opacity-90">
                  <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-2 flex items-center gap-1">
                    <CurrencyDollar size={12} weight="duotone" /> Invite a creator — earn credits
                  </div>
                  <div className="bg-[#FDFCF8] border-2 border-dashed hairline rounded-md p-4 mb-3 text-center">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">Locked</div>
                    <div className="text-sm text-[#1C1B1A] font-medium mt-1">Unlock Creator Pro</div>
                    <div className="text-[11px] text-[#6B6A68] mt-1.5 leading-relaxed">
                      Creator Pro unlocks trackable invite links.
                      Until then, signups from your shares credit the silo's creator.
                    </div>
                    <button
                      onClick={() => setShowAffiliateExplainer(true)}
                      data-testid="invite-affiliate-upsell"
                      className="mt-3 text-xs px-4 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] inline-flex items-center gap-1.5"
                    >
                      What's Creator Pro? <CaretRight size={12} />
                    </button>
                  </div>
                </section>
              )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* iter-33: Affiliate Creator explainer — shared modal */}
      <AffiliateExplainer
        open={showAffiliateExplainer}
        onClose={() => setShowAffiliateExplainer(false)}
        onUpgrade={async () => {
          // Pass the current silo as the upgrade source so credit is captured
          // at the moment of platform-checkout.
          if (!isAuthed) {
            nav(`/signup?from_silo=${encodeURIComponent(silo.slug)}`);
            return;
          }
          try {
            const r = await api.post("/stripe/platform/checkout", {
              origin_url: window.location.origin,
              return_path: window.location.pathname,
              upgrade_source_silo_slug: silo.slug,
            });
            window.location.href = r.data.url;
          } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start checkout.");
          }
        }}
      />
    </>
  );
}
